/**
 * Client binding for `generateAiSynthese` + hook for the Analyse IA tab.
 *
 * Lifecycle :
 *   1. On mount, call the server-side `generateAiSynthese({ ficheId })`. The
 *      function handles cache-vs-regen entirely server-side (it compares
 *      the current fiche's structural `inputsHash` against the cached doc
 *      under `/users/{uid}/fiches/{ficheId}/ai/synthese`).
 *   2. In parallel, subscribe to that same cache doc so that if a background
 *      writer (future: Cloud Function trigger on PLU revision) refreshes
 *      the synthèse, the UI swaps live without a reload.
 *
 * Why not just subscribe and skip the callable ? We can't tell client-side
 * whether the cached doc's hash is stale — duplicating the structural hash
 * derivation would mean keeping the TS and Node versions in lockstep. So the
 * callable is the single source of truth for "is this synthèse still valid
 * for these inputs", and a miss triggers regen + cache rewrite.
 *
 * Cost : ~1 Firestore read (cache check) per tab-open. Claude call only on
 * hash-mismatch (material edit to the fiche).
 */

import { useEffect, useState } from "react";
import {
  doc,
  onSnapshot,
  type DocumentData,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "../firebase";

// ─── Doc shape (keep in sync with functions/src/generateAiSynthese.ts) ───
export type AiSynthese = {
  version: 1;
  content: string;
  generatedAt: string; // ISO-8601
  model: string;
  inputsHash: string;
  pdfInclude: boolean;
};

const callGenerateAiSynthese = httpsCallable<
  { ficheId: string },
  AiSynthese
>(functions, "generateAiSynthese");

// ─── Hook ────────────────────────────────────────────────────────────────
type State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; data: AiSynthese; fromServer: boolean }
  | { status: "error"; message: string };

export function useAiSynthese(ficheId: string | null): State {
  const [state, setState] = useState<State>({ status: "idle" });

  useEffect(() => {
    if (!ficheId) {
      setState({ status: "idle" });
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    // ── Snapshot subscription ───────────────────────────────────────
    // Picks up any server-side write instantly, including the one the
    // callable will perform on a regen. If a cached doc exists we surface
    // it immediately so the UI doesn't spin while the callable verifies
    // freshness — the callable's response will either match (no-op) or
    // replace this via the same snapshot (stale → regen path).
    const ref = doc(
      db,
      "users",
      user.uid,
      "fiches",
      ficheId,
      "ai",
      "synthese",
    );
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (cancelled) return;
        if (!snap.exists()) return; // keep loading; callable will populate
        const parsed = parseAiSynthese(snap.data());
        if (parsed) {
          setState({ status: "ready", data: parsed, fromServer: false });
        }
      },
      (err) => {
        // Non-fatal — the callable path below may still succeed.
        console.warn("[ai/synthese] snapshot error", err);
      },
    );

    // ── Callable (authoritative freshness check) ────────────────────
    callGenerateAiSynthese({ ficheId })
      .then((res) => {
        if (cancelled) return;
        setState({
          status: "ready",
          data: res.data as AiSynthese,
          fromServer: true,
        });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // Only surface the error if we haven't already rendered a cached
        // doc via snapshot — a stale-but-readable synthèse beats a red
        // banner when Claude is having a bad minute.
        setState((prev) =>
          prev.status === "ready"
            ? prev
            : { status: "error", message: extractMsg(e) },
        );
      });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [ficheId]);

  return state;
}

// ─── Helpers ─────────────────────────────────────────────────────────────
function parseAiSynthese(raw: DocumentData): AiSynthese | null {
  if (!raw || raw.version !== 1) return null;
  const c = raw.content;
  if (typeof c !== "string" || c.length === 0) return null;
  return {
    version: 1,
    content: c,
    generatedAt: typeof raw.generatedAt === "string" ? raw.generatedAt : "",
    model: typeof raw.model === "string" ? raw.model : "",
    inputsHash: typeof raw.inputsHash === "string" ? raw.inputsHash : "",
    pdfInclude: Boolean(raw.pdfInclude),
  };
}

function extractMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "object" && e && "message" in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return String(e);
}
